import { VariableBenefitSourceJurisdiction } from '../../../../../models/submissions/variable-benefit-source-jurisdiction';
import { LockedUnlockedBalance } from '../../../../../models/shared/locked-unlocked-balance';
import {
  effectiveLockedBalance,
  effectiveUnlockedBalance
} from '../../account-asset-selection/utils/asset-selection-rules';

export interface VbSourceJurisdictionAmounts {
  hasUnlock: boolean;
  originalLocked: number;
  effectiveLocked: number;
  originalUnlocked: number;
  effectiveUnlocked: number;
}

/**
 * Locked/unlocked amounts a VB source contributes, before and after any unlock. Gross balances
 * come from the source's server snapshot when submitted, otherwise from the member's live
 * balances (the in-flow review). The unlock is always taken against the gross locked-in balance,
 * matching asset-selection-rules.ts.
 */
export function vbSourceJurisdictionAmounts(
  source: VariableBenefitSourceJurisdiction,
  balance: LockedUnlockedBalance | null
): VbSourceJurisdictionAmounts {
  const hasUnlock = source.lockedAmountToUnlock !== null || source.lockedPercentageToUnlock !== null;
  const grossLocked = source.availableLockedBalance ?? balance?.lockedInBalance ?? null;
  const grossUnlocked = source.availableUnlockedBalance ?? balance?.unlockedBalance ?? null;
  // A manual desired amount is already the net figure chosen against the post-unlock balance, so
  // it is never reduced again by the unlocked portion.
  const desiredLocked = source.desiredLockedStartBalance ?? 0;
  const desiredUnlocked = source.desiredUnlockedStartBalance ?? 0;

  const originalLocked =
    grossLocked !== null && (source.useEntireLockedBalance || hasUnlock) ? grossLocked : desiredLocked;
  const effectiveLocked =
    grossLocked !== null && source.useEntireLockedBalance
      ? effectiveLockedBalance(grossLocked, source.lockedAmountToUnlock, source.lockedPercentageToUnlock)
      : desiredLocked;
  const originalUnlocked = grossUnlocked !== null && source.useEntireUnlockedBalance ? grossUnlocked : desiredUnlocked;
  // Unlocked funds only count as used when the entire non-locked-in balance is selected; otherwise
  // they stay in the member's DC account.
  const effectiveUnlocked =
    grossUnlocked !== null && source.useEntireUnlockedBalance
      ? effectiveUnlockedBalance(
          grossLocked ?? 0,
          grossUnlocked,
          source.lockedAmountToUnlock,
          source.lockedPercentageToUnlock
        )
      : desiredUnlocked;

  return { hasUnlock, originalLocked, effectiveLocked, originalUnlocked, effectiveUnlocked };
}
